import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useLocalizedHref } from '@/shared/hooks/useLocalizedHref'
import { cn } from '@/shared/lib/cn'
import { useLocaleStore } from '@/shared/store/localeStore'

import {
  getHelpCategoryByKey,
  helpCategories,
  helpPath,
  type HelpCategoryKey,
} from '../data/helpCategories'

interface HelpLayoutProps {
  currentKey: HelpCategoryKey
  children: ReactNode
}

export function HelpLayout({ currentKey, children }: HelpLayoutProps) {
  const { t } = useTranslation()
  const locale = useLocaleStore((s) => s.locale)
  const helpHubHref = useLocalizedHref('help-hub')
  const current = getHelpCategoryByKey(currentKey)

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mb-6 text-sm">
        <ol className="flex flex-wrap items-center gap-2 text-muted-foreground">
          <li>
            <Link to={helpHubHref} className="hover:text-red">
              {t('help.common.breadcrumbRoot')}
            </Link>
          </li>
          <li aria-hidden>›</li>
          <li className="font-medium text-foreground">
            {current ? t(current.titleKey) : currentKey}
          </li>
        </ol>
      </nav>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-[200px_1fr]">
        {/* Nav lateral solo en md+ */}
        <aside className="hidden md:block">
          <ul className="space-y-2">
            {helpCategories.map((cat) => {
              const active = cat.key === currentKey
              return (
                <li key={cat.key}>
                  <Link
                    to={helpPath(cat.key, locale)}
                    className={cn(
                      'brutal-sm flex items-center gap-2 rounded-md px-3 py-2 font-display text-sm font-bold transition-transform',
                      active
                        ? 'bg-foreground text-background'
                        : 'bg-card text-foreground hover:-translate-y-0.5',
                    )}
                  >
                    {active && <span aria-hidden>●</span>}
                    {t(cat.titleKey)}
                  </Link>
                </li>
              )
            })}
          </ul>
        </aside>

        {/* Contenido */}
        <article>{children}</article>
      </div>
    </div>
  )
}
